import React from "react";
import QRCode from "react-qr-code";

const DYNAMIC_FIELD_LABELS = {
    equipment_name: "Matériel", equipment_id: "ID Matériel", serial_number: "Numéro de série",
    defect_type: "Type de défaut", criticality: "Criticité", since_when: "Depuis quand",
    workaround_available: "Contournement disponible", workaround_description: "Description du contournement",
    date_start: "Date de début", date_end: "Date de fin", destination: "Destination", motif: "Motif",
    good_condition_confirmed: "Bon état confirmé", returned_date: "Date de retour", return_condition: "État au retour",
    account_name: "Nom du compte", issue_type: "Type de problème", verification_method: "Méthode de vérification",
    transmission_method: "Méthode de transmission", software_name: "Logiciel", software_version: "Version",
    software_category: "Catégorie logiciel", justification: "Justification", access_type: "Type d'accès",
    resource_name: "Ressource", start_date: "Date de début", end_date: "Date de fin",
    employee_name: "Nom de l'employé", employee_email: "Email de l'employé", position: "Poste",
    manager_name: "Manager", department_name: "Département", description: "Description",
    impact: "Impact", urgency: "Urgence", frequency: "Fréquence", affected_users: "Utilisateurs affectés",
    change_type: "Type de changement", risk_level: "Niveau de risque", rollback_plan: "Plan de rollback",
    implementation_date: "Date de mise en œuvre",
    request_subject: "Objet de la demande",
    license_type: "Type de licence", license_count: "Nombre de licences",
    license_beneficiaries: "Bénéficiaire(s) de la licence",
};

const STATUS_SYMBOLS = {
    draft: "○", open: "●", pending_approval: "◐", in_progress: "◑",
    pending_user: "◔", resolved: "◉", closed: "◈", cancelled: "⊘",
};

const STATUS_LABELS = {
    draft: "Brouillon", open: "Ouvert", pending_approval: "En attente d'approbation",
    in_progress: "En cours", pending_user: "En attente utilisateur",
    resolved: "Résolu", closed: "Clôturé", cancelled: "Annulé",
};

const PRIORITY_LABELS = { low: "Basse", medium: "Moyenne", high: "Haute", critical: "Critique" };

function formatDateFR(dateStr) {
    if (!dateStr) return "—";
    try {
        return new Date(dateStr).toLocaleDateString("fr-FR", {
            day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return dateStr; }
}

function formatDateShort(dateStr) {
    if (!dateStr) return dateStr;
    try { return new Date(dateStr).toLocaleDateString("fr-FR"); }
    catch { return dateStr; }
}

function isISODate(v) {
    if (typeof v !== "string") return false;
    return /^\d{4}-\d{2}-\d{2}(T|$)/.test(v);
}

function formatFieldValue(value) {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "boolean") return value ? "Oui" : "Non";
    if (isISODate(value)) return formatDateShort(value);
    if (Array.isArray(value)) {
        const mapped = value.map((item) => {
            if (typeof item === "object" && item !== null) {
                return Object.entries(item)
                    .filter(([k]) => !["software_id", "chocolatey_id", "is_custom"].includes(k))
                    .map(([k, v]) => `${getFieldLabel(k)}: ${formatFieldValue(v) ?? "—"}`)
                    .join(" | ");
            }
            return String(item);
        });
        return mapped.join(", ");
    }
    if (typeof value === "object" && value !== null) {
        return Object.entries(value)
            .map(([k, v]) => `${getFieldLabel(k)}: ${formatFieldValue(v) ?? "—"}`)
            .join(", ");
    }
    return String(value);
}

function getFieldLabel(key) {
    return DYNAMIC_FIELD_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function isPlaceholderEmail(email) {
    return !email || email.includes("@no-email.local");
}

function PersonPhones({ user }) {
    if (!user) return null;
    const parts = [];
    if (user.phone_fixed) parts.push(`Fixe: ${user.phone_fixed}`);
    if (user.phone_mobile) parts.push(`Mobile: ${user.phone_mobile}`);
    if (!isPlaceholderEmail(user.email) && user.user_type === "external") parts.push(`Email: ${user.email}`);
    if (parts.length === 0) return null;
    return <span style={{ fontSize: "0.65rem", color: "#6b7280" }}> ({parts.join(" | ")})</span>;
}

export function TicketPrintView({ ticket, getUserById, getTicketTypeConfig, departments }) {
    if (!ticket) return null;

    const ticketUrl = `${window.location.origin}/tickets/${ticket.id}`;
    const typeConfig = getTicketTypeConfig(ticket.type);
    const requester = getUserById(ticket.requester_id);
    const beneficiary = ticket.beneficiary_id ? getUserById(ticket.beneficiary_id) : null;
    const assignee = ticket.assigned_to_id ? getUserById(ticket.assigned_to_id) : null;
    const reqDept = requester?.department_id ? departments?.find((d) => d.id === requester.department_id) : null;
    const benDept = beneficiary?.department_id ? departments?.find((d) => d.id === beneficiary.department_id) : null;
    const checklists = ticket.checklists || [];
    const solutions = ticket.solutions || [];
    const retainedSolution = solutions.find(s => s.is_retained);
    const dlInfo = ticket.deadline_info || {};
    const dynamicEntries = ticket.dynamic_fields
        ? Object.entries(ticket.dynamic_fields).filter(([, v]) => v !== null && v !== undefined && v !== "")
        : [];
    const isResolved = ticket.status === "resolved" || ticket.status === "closed";
    const visibleComments = (ticket.comments || []).filter((c) => !c.is_internal);
    const resolutionComment = isResolved
        ? [...visibleComments].reverse().find((c) => !c.is_system && !c.contact_type && c.author_id !== ticket.requester_id)
        : null;
    const attachmentCount = (ticket.attachments || []).length;
    const commentCount = visibleComments.filter((c) => !c.is_system && !c.contact_type).length;

    return (
        <div className="print-view" data-testid="ticket-print-view">
            {/* ARCHIVED BANNER */}
            {ticket.archived_at && (
                <div style={{ textAlign: "center", padding: "8px 0", marginBottom: "8px", backgroundColor: "#e5e7eb", border: "2px solid #9ca3af", fontWeight: "bold", fontSize: "14px", letterSpacing: "2px", color: "#374151" }} data-testid="print-archived-banner">
                    ARCHIVÉ
                </div>
            )}

            {/* BLOC 1 — HEADER */}
            <div className="print-header">
                <div className="print-header-left">
                    <div className="print-logo">EGG TELSA</div>
                    <div className="print-subtitle">Service IT — Système de ticketing</div>
                </div>
                <div className="print-header-right">
                    <div className="print-ticket-number">{ticket.ticket_number}</div>
                    <QRCode value={ticketUrl} size={80} level="M" />
                </div>
            </div>
            <hr className="print-separator" />

            {/* BLOC 2 — INFORMATIONS CLÉS */}
            <div className="print-section">
                <table className="print-info-grid">
                    <tbody>
                        <tr>
                            <td className="print-info-label">Type de demande</td>
                            <td className="print-info-value">{typeConfig.label}</td>
                            <td className="print-info-label">Statut</td>
                            <td className="print-info-value">{STATUS_SYMBOLS[ticket.status] || "●"} {STATUS_LABELS[ticket.status] || ticket.status}</td>
                        </tr>
                        <tr>
                            <td className="print-info-label">Priorité</td>
                            <td className="print-info-value">{PRIORITY_LABELS[ticket.priority] || ticket.priority}</td>
                            {ticket.category ? (
                                <><td className="print-info-label">Catégorie</td><td className="print-info-value">{ticket.category}</td></>
                            ) : (<td colSpan={2} />)}
                        </tr>
                        <tr>
                            <td className="print-info-label">Date de création</td>
                            <td className="print-info-value">{formatDateFR(ticket.created_at)}</td>
                            <td className="print-info-label">Date de résolution</td>
                            <td className="print-info-value">{ticket.resolved_at ? formatDateFR(ticket.resolved_at) : "—"}</td>
                        </tr>
                        <tr>
                            <td className="print-info-label">Date limite</td>
                            <td className="print-info-value">{dlInfo.has_deadline ? formatDateFR(dlInfo.deadline) : "—"}</td>
                            <td className="print-info-label">Statut deadline</td>
                            <td className="print-info-value">{dlInfo.has_deadline ? (dlInfo.is_overdue ? "Hors délai" : "Dans les temps") : "—"}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* BLOC 3 — PERSONNES */}
            <div className="print-section">
                <table className="print-info-grid">
                    <tbody>
                        <tr>
                            <td className="print-info-label">Demandeur</td>
                            <td className="print-info-value">
                                {requester ? `${requester.first_name} ${requester.last_name}` : "Inconnu"}
                                {requester?.user_type === "external" && " (EXT)"}
                                {reqDept ? ` — ${reqDept.name}` : ""}
                                <PersonPhones user={requester} />
                            </td>
                            <td className="print-info-label">Assigné à</td>
                            <td className="print-info-value">
                                {assignee ? `${assignee.first_name} ${assignee.last_name}` : "Non assigné"}
                                <PersonPhones user={assignee} />
                            </td>
                        </tr>
                        {beneficiary && beneficiary.id !== ticket.requester_id && (
                            <tr>
                                <td className="print-info-label">Bénéficiaire</td>
                                <td className="print-info-value">
                                    {beneficiary.first_name} {beneficiary.last_name}
                                    {beneficiary.user_type === "external" && " (EXT)"}
                                    {benDept ? ` — ${benDept.name}` : ""}
                                    <PersonPhones user={beneficiary} />
                                </td>
                                <td className="print-info-label" style={{fontSize: "0.65rem", color: "#6b7280"}}>Créé par</td>
                                <td className="print-info-value" style={{fontSize: "0.65rem", color: "#6b7280"}}>
                                    {requester ? `${requester.first_name} ${requester.last_name}` : ""}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* BLOC 4 — DESCRIPTION */}
            <div className="print-section">
                <h2 className="print-section-title">{ticket.title}</h2>
                <p className="print-body">{ticket.description || "Aucune description"}</p>
            </div>

            {/* BLOC 5 — DYNAMIC FIELDS */}
            {dynamicEntries.length > 0 && (
                <div className="print-section">
                    <h3 className="print-section-subtitle">Détails de la demande</h3>
                    <table className="print-fields-table">
                        <tbody>
                            {dynamicEntries.map(([key, value]) => {
                                const formatted = formatFieldValue(value);
                                if (!formatted) return null;
                                return (
                                    <tr key={key}>
                                        <td className="print-field-label">{getFieldLabel(key)}</td>
                                        <td className="print-field-value">{formatted}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* BLOC 6 — CHECKLISTS */}
            {checklists.length > 0 && (
                <div className="print-section">
                    <h3 className="print-section-subtitle">Checklist</h3>
                    {checklists.map((cl) => {
                        const completed = cl.items.filter((i) => i.is_completed).length;
                        return (
                            <div key={cl.id} className="print-checklist">
                                <div className="print-checklist-header">
                                    <strong>{cl.title}</strong>
                                    <span className="print-checklist-progress">{completed}/{cl.items.length} complétés</span>
                                </div>
                                {cl.items.map((item) => (
                                    <div key={item.id} className="print-checklist-item">
                                        <span>{item.is_completed ? "☑" : "☐"}</span>
                                        <span className={item.is_completed ? "print-completed" : ""}>{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* BLOC 6B — SOLUTIONS */}
            {solutions.length > 0 && (
                <div className="print-section">
                    <h3 className="print-section-subtitle">Solutions</h3>
                    {retainedSolution && (
                        <div className="print-checklist" style={{ borderLeft: "3px solid #16a34a", paddingLeft: "12px", marginBottom: "8px" }}>
                            <p className="print-body" style={{ fontWeight: 600 }}>&#10003; Solution retenue</p>
                            <p className="print-body">{retainedSolution.content}</p>
                            <p className="print-meta">Par {getUserById(retainedSolution.author_id)?.first_name} {getUserById(retainedSolution.author_id)?.last_name} — {formatDateFR(retainedSolution.created_at)}</p>
                        </div>
                    )}
                    {solutions.filter(s => !s.is_retained).map((sol) => (
                        <div key={sol.id} className="print-checklist" style={{ marginBottom: "8px" }}>
                            <p className="print-body">{sol.content}</p>
                            <p className="print-meta">Par {getUserById(sol.author_id)?.first_name} {getUserById(sol.author_id)?.last_name} — {formatDateFR(sol.created_at)}</p>
                        </div>
                    ))}
                </div>
            )}

            {/* BLOC 7 — RÉSOLUTION */}
            {isResolved && (
                <div className="print-section">
                    <h3 className="print-section-subtitle">Résolution</h3>
                    {resolutionComment ? (
                        <div>
                            <p className="print-body">{resolutionComment.content}</p>
                            <p className="print-meta">
                                Par {getUserById(resolutionComment.author_id)?.first_name} {getUserById(resolutionComment.author_id)?.last_name} — {formatDateFR(resolutionComment.created_at)}
                            </p>
                        </div>
                    ) : (
                        <p className="print-body print-muted">Résolu sans commentaire de résolution.</p>
                    )}
                    {ticket.resolved_at && <p className="print-meta">Résolu le {formatDateFR(ticket.resolved_at)}</p>}
                </div>
            )}

            {/* BLOC 8 — FOOTER */}
            <hr className="print-separator print-footer-sep" />
            <div className="print-footer">
                <span>Document généré le {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} — EGG TELSA SA — Système de ticketing IT</span>
                <span>
                    {attachmentCount > 0 && `${attachmentCount} pièce(s) jointe(s) · `}
                    {commentCount} commentaire(s)
                </span>
            </div>
        </div>
    );
}
